import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

const IS_ALPHA = import.meta.env.VITE_IS_ALPHA === 'true';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';
import { LandingProblem } from '../components/Landing/LandingProblem';
import { LandingSteps } from '../components/Landing/LandingSteps';
import { LandingModels } from '../components/Landing/LandingModels';
import { LandingDifferentiators } from '../components/Landing/LandingDifferentiators';
import { LandingCollab } from '../components/Landing/LandingCollab';
import { LandingPricing } from '../components/Landing/LandingPricing';
import { LandingFaq } from '../components/Landing/LandingFaq';
import { LandingCta } from '../components/Landing/LandingCta';

/* ──────────────────────────────────────────────────────────────────────────
   ELEVATED Design System: Ethereal Glassmorphism + Prism Modern + Calm Tech
   ─────────────────────────────────────────────────────────────────────────
   Base:    Proclean White — pure white canvas, cool undertone
   Glass:   Real backdrop-blur HTML divs — not SVG fakes
   Palette: Soft blue + pink/lavender for contrast (#daecf6, #eee3f3, #bfadd3)
   Depth:   Z-layered nodes with overlapping, varied shadows
   Texture: Subtle dot grid background to elevate nodes
   Mood:    Apple-level restraint, editorial elegance
   Font:    Playfair Display (headline serif) + Inter (body sans)
   Brand:   #1d2939 (Dark Navy) — signature CTA / text color
   Accent:  #3e4557 — secondary dark tone
   Rule:    NEVER use gradients in text — solid colors only
   ────────────────────────────────────────────────────────────────────────── */

const C = {
  prompt:   { accent: '#7ea8c9', bg: 'rgba(126,168,201,0.07)', border: 'rgba(126,168,201,0.13)' },
  upload:   { accent: '#b5a0cc', bg: 'rgba(181,160,204,0.07)', border: 'rgba(181,160,204,0.13)' },
  generate: { accent: '#3e4557', bg: 'rgba(62,69,87,0.06)',    border: 'rgba(62,69,87,0.12)' },
  output:   { accent: '#b5a0cc', bg: 'rgba(181,160,204,0.07)', border: 'rgba(181,160,204,0.13)' },
};

const TEXT = { primary: '#1d2939', secondary: '#5a6478', muted: '#8c95a6' };


/* ──────────────────────────────────────────────────────────────────────────
   Desktop Hero — Drag-and-drop HTML nodes + dynamic SVG edges
   Real backdrop-filter glassmorphism, z-layered depth, interactive
   ────────────────────────────────────────────────────────────────────────── */

/* Canvas logical dimensions — sized to encompass all node positions with padding */
const CANVAS_W = 1160;
const CANVAS_H = 620;

/* Node sizes are fixed; positions are in pixel coords within the canvas */
interface NodeDef { x: number; y: number; w: number; h: number; z: number }

/* ── Responsive breakpoint positions ─────────────────────────────────────
   Each entry defines node positions at a specific container width.
   Between breakpoints, positions are linearly interpolated.
   Below the smallest breakpoint, the smallest is used.
   Above the largest breakpoint, the largest is used.
   ────────────────────────────────────────────────────────────────────────── */
type NodePositions = Record<string, { x: number; y: number }>;

interface Breakpoint {
  /** Container width in CSS pixels at which these positions are ideal */
  width: number;
  positions: NodePositions;
}

/* Sorted ascending by width. User provides exact positions per breakpoint. */
const BREAKPOINT_NODES: Breakpoint[] = [
  /* ── 928px — smallest desktop (1024px viewport minus padding) ── */
  {
    width: 928,
    positions: {
      prompt:   { x: 40,  y: 142 },
      upload:   { x: 44,  y: 442 },
      generate: { x: 459, y: 238 },
      out1:     { x: 949, y: 55 },
      out2:     { x: 938, y: 383 },
    },
  },
  /* ── 1100px — standard desktop ── */
  {
    width: 1100,
    positions: {
      prompt:   { x: 20,  y: 100 },
      upload:   { x: 20,  y: 380 },
      generate: { x: 450, y: 230 },
      out1:     { x: 930, y: 30 },
      out2:     { x: 920, y: 340 },
    },
  },
  /* ── 1440px+ — full desktop ── */
  {
    width: 1440,
    positions: {
      prompt:   { x: -31,  y: 46 },
      upload:   { x: -42,  y: 315 },
      generate: { x: 413,  y: 219 },
      out1:     { x: 924,  y: -15 },
      out2:     { x: 820,  y: 280 },
    },
  },
];

/* Static node dimensions (same at all breakpoints) */
const NODE_SIZES: Record<string, { w: number; h: number; z: number }> = {
  prompt:   { w: 196, h: 138, z: 3 },
  upload:   { w: 196, h: 140, z: 2 },
  generate: { w: 250, h: 205, z: 5 },
  out1:     { w: 195, h: 210, z: 4 },
  out2:     { w: 195, h: 200, z: 3 },
};

/** Linearly interpolate node positions based on container width */
function lerpNodes(containerWidth: number): Record<string, NodeDef> {
  const bps = BREAKPOINT_NODES;
  const keys = Object.keys(NODE_SIZES);

  // Clamp to first/last breakpoint
  if (containerWidth <= bps[0].width) {
    return Object.fromEntries(keys.map(k => [k, { ...NODE_SIZES[k], ...bps[0].positions[k] }]));
  }
  if (containerWidth >= bps[bps.length - 1].width) {
    return Object.fromEntries(keys.map(k => [k, { ...NODE_SIZES[k], ...bps[bps.length - 1].positions[k] }]));
  }

  // Find the two surrounding breakpoints
  let lo = bps[0], hi = bps[1];
  for (let i = 1; i < bps.length; i++) {
    if (bps[i].width >= containerWidth) {
      lo = bps[i - 1];
      hi = bps[i];
      break;
    }
  }

  const t = (containerWidth - lo.width) / (hi.width - lo.width);
  return Object.fromEntries(keys.map(k => {
    const a = lo.positions[k];
    const b = hi.positions[k];
    return [k, {
      ...NODE_SIZES[k],
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
    }];
  }));
}

/* Edge definitions: from node → side → to node → side */
const EDGE_DEFS = [
  { from: 'prompt',   side: 'right', to: 'generate', toSide: 'left', color: '#5b8ab5', id: 'ep' },
  { from: 'upload',   side: 'right', to: 'generate', toSide: 'left', color: '#9678b4', id: 'eu' },
  { from: 'generate', side: 'right', to: 'out1',     toSide: 'left', color: '#7b6ba0', id: 'eg1' },
  { from: 'generate', side: 'right', to: 'out2',     toSide: 'left', color: '#7b6ba0', id: 'eg2' },
];

/* Compute anchor point on a node edge */
function anchor(n: NodeDef, side: string): { x: number; y: number } {
  switch (side) {
    case 'right':  return { x: n.x + n.w, y: n.y + n.h / 2 };
    case 'left':   return { x: n.x,       y: n.y + n.h / 2 };
    case 'top':    return { x: n.x + n.w / 2, y: n.y };
    case 'bottom': return { x: n.x + n.w / 2, y: n.y + n.h };
    default:       return { x: n.x + n.w / 2, y: n.y + n.h / 2 };
  }
}

function HeroCanvasDesktop() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(CANVAS_W);
  const [nodes, setNodes] = useState(() => lerpNodes(CANVAS_W));
  const [dragging, setDragging] = useState<{ key: string; ox: number; oy: number } | null>(null);
  const userDragged = useRef(false);

  /* Track container width via ResizeObserver */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? CANVAS_W;
      setContainerWidth(w);
    });
    ro.observe(el);
    setContainerWidth(el.getBoundingClientRect().width || CANVAS_W);
    return () => ro.disconnect();
  }, []);

  /* Recalculate positions when container resizes (unless user is dragging) */
  useEffect(() => {
    if (!userDragged.current) {
      setNodes(lerpNodes(containerWidth));
    }
  }, [containerWidth]);

  /* Compute edges dynamically from node positions */
  const edges = EDGE_DEFS.map(e => {
    const a = anchor(nodes[e.from], e.side);
    const b = anchor(nodes[e.to], e.toSide);
    return { ...e, x1: a.x, y1: a.y, x2: b.x, y2: b.y };
  });

  /* Drag handlers */
  const onPointerDown = useCallback((key: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    userDragged.current = true;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const scale = rect.width / CANVAS_W;
    const mx = (e.clientX - rect.left) / scale;
    const my = (e.clientY - rect.top) / scale;
    setDragging({ key, ox: mx - nodes[key].x, oy: my - nodes[key].y });
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [nodes]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const scale = rect.width / CANVAS_W;
    const mx = (e.clientX - rect.left) / scale;
    const my = (e.clientY - rect.top) / scale;
    const node = nodes[dragging.key];
    const nx = Math.max(-node.w * 0.3, Math.min(CANVAS_W - node.w * 0.7, mx - dragging.ox));
    const ny = Math.max(-node.h * 0.5, Math.min(CANVAS_H - node.h * 0.3, my - dragging.oy));
    setNodes(prev => ({ ...prev, [dragging.key]: { ...prev[dragging.key], x: nx, y: ny } }));
  }, [dragging, nodes]);

  const onPointerUp = useCallback(() => {
    setDragging(null);
  }, []);

  /* Stop floating animation while dragging */
  const nodeClass = (key: string) =>
    dragging?.key === key ? 'hero-node-dragging' : 'hero-glass-node';

  return (
    <div
      ref={containerRef}
      className="hero-canvas-desktop"
      style={{ position: 'relative', width: '100%', maxWidth: CANVAS_W, margin: '0 auto', aspectRatio: `${CANVAS_W}/${CANVAS_H}`, touchAction: 'none', overflow: 'visible' }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >

      {/* ── SVG layer: dynamic edges ─────────────────────── */}
      <svg viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`} fill="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
        <defs>
          <linearGradient id="prismEdge" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#5b8ab5" stopOpacity="0.7" />
            <stop offset="25%" stopColor="#9678b4" stopOpacity="0.65" />
            <stop offset="50%" stopColor="#4a9dd4" stopOpacity="0.6" />
            <stop offset="75%" stopColor="#b5a0cc" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#5b8ab5" stopOpacity="0.7" />
          </linearGradient>
          <filter id="edgeGlow" x="-25%" y="-80%" width="150%" height="260%">
            <feGaussianBlur stdDeviation="8" />
          </filter>
        </defs>
        {edges.map(e => {
          const dx = (e.x2 - e.x1) * 0.45;
          const dy = (e.y2 - e.y1) * 0.45;
          /* Smarter control points depending on direction */
          const horizontal = Math.abs(e.x2 - e.x1) > Math.abs(e.y2 - e.y1);
          const c1x = horizontal ? e.x1 + dx : e.x1;
          const c1y = horizontal ? e.y1 : e.y1 + dy;
          const c2x = horizontal ? e.x2 - dx : e.x2;
          const c2y = horizontal ? e.y2 : e.y2 - dy;
          const d = `M${e.x1},${e.y1} C${c1x},${c1y} ${c2x},${c2y} ${e.x2},${e.y2}`;
          return (
            <g key={e.id}>
              {/* Wide prismatic glow — reduced 20% */}
              <path d={d} stroke="url(#prismEdge)" strokeWidth="13" filter="url(#edgeGlow)" fill="none" opacity="0.55" />
              {/* Core visible line */}
              <path id={e.id} d={d} stroke={e.color} strokeWidth="2" opacity="0.45" fill="none" strokeLinecap="round">
                <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="3s" repeatCount="indefinite" />
              </path>
              {/* Traveling light */}
              <circle r="4.5" fill={e.color} opacity="0.8">
                <animateMotion dur="5s" repeatCount="indefinite"><mpath href={`#${e.id}`} /></animateMotion>
              </circle>
            </g>
          );
        })}
      </svg>

      {/* ── PROMPT NODE ─────────────────────────────────── */}
      <div className={nodeClass('prompt')} onPointerDown={e => onPointerDown('prompt', e)} style={{ position: 'absolute', left: `${(nodes.prompt.x / CANVAS_W) * 100}%`, top: `${(nodes.prompt.y / CANVAS_H) * 100}%`, width: `${(nodes.prompt.w / CANVAS_W) * 100}%`, zIndex: nodes.prompt.z, cursor: 'grab', userSelect: 'none' }}>
        <GlassCard accent={C.prompt} label="Prompt" shadow="soft">
          <p style={{ color: TEXT.primary, fontSize: 11.5, lineHeight: 1.55, margin: 0, opacity: 0.85 }}>
            Urban fashion shoot in neon-lit city streets, cyberpunk aesthetic, rain reflections
          </p>
          <div className="hero-cursor" style={{ width: 1.5, height: 13, background: C.prompt.accent, borderRadius: 1, opacity: 0.5, marginTop: 2, display: 'inline-block' }} />
          <div style={{ marginTop: 8 }}>
            <span style={{ background: C.prompt.bg, color: C.prompt.accent, fontSize: 9, fontWeight: 600, padding: '3px 10px', borderRadius: 4 }}>Enhance</span>
          </div>
        </GlassCard>
      </div>

      {/* ── UPLOAD NODE ─────────────────────────────────── */}
      <div className={nodeClass('upload')} onPointerDown={e => onPointerDown('upload', e)} style={{ position: 'absolute', left: `${(nodes.upload.x / CANVAS_W) * 100}%`, top: `${(nodes.upload.y / CANVAS_H) * 100}%`, width: `${(nodes.upload.w / CANVAS_W) * 100}%`, zIndex: nodes.upload.z, cursor: 'grab', userSelect: 'none' }}>
        <GlassCard accent={C.upload} label="Upload" shadow="soft" noPadding>
          <div style={{ display: 'flex', gap: 2 }}>
            <div style={{ flex: 1, aspectRatio: '1', overflow: 'hidden', background: '#f0f0f0' }}>
              <img src="/images/hero/subject.png" alt="Model reference" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
            <div style={{ flex: 1, aspectRatio: '1', overflow: 'hidden', background: '#f0f0f0' }}>
              <img src="/images/hero/product.jpeg" alt="Product reference" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
          </div>
          <p style={{ color: TEXT.muted, fontSize: 9.5, padding: '6px 10px', margin: 0, background: 'rgba(255,255,255,0.3)' }}>2 references</p>
        </GlassCard>
      </div>

      {/* ── GENERATE NODE ── HERO FOCAL POINT ─────────── */}
      <div className={`${nodeClass('generate')} hero-generate`} onPointerDown={e => onPointerDown('generate', e)} style={{ position: 'absolute', left: `${(nodes.generate.x / CANVAS_W) * 100}%`, top: `${(nodes.generate.y / CANVAS_H) * 100}%`, width: `${(nodes.generate.w / CANVAS_W) * 100}%`, zIndex: nodes.generate.z, cursor: 'grab', userSelect: 'none' }}>
        {/* Prismatic halo glow */}
        <div className="hero-prism-halo" style={{
          position: 'absolute', inset: -12, borderRadius: 20, zIndex: -1,
          background: 'conic-gradient(from 180deg, rgba(126,168,201,0.14), rgba(181,160,204,0.12), rgba(138,179,210,0.10), rgba(191,173,211,0.12), rgba(126,168,201,0.14))',
          filter: 'blur(18px)',
        }} />
        <div style={{
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 16,
          border: '1px solid rgba(255,255,255,0.8)',
          boxShadow: '0 8px 40px rgba(62,69,87,0.12), 0 1.5px 6px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)',
          overflow: 'hidden', width: '100%',
        }}>
          {/* Header */}
          <div style={{
            padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.04)',
            background: 'rgba(255,255,255,0.5)',
          }}>
            <span style={{ color: C.generate.accent, fontSize: 13, fontWeight: 700, fontFamily: 'Inter,system-ui,sans-serif', letterSpacing: '-0.01em' }}>Generate</span>
            <span style={{
              background: C.generate.bg, padding: '2.5px 10px', borderRadius: 10,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              <span className="hero-status-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: C.generate.accent, display: 'block' }} />
              <span style={{ color: C.generate.accent, fontSize: 10, fontWeight: 600 }}>Ready</span>
            </span>
          </div>
          {/* Body */}
          <div style={{ padding: '14px 16px' }}>
            {[
              { k: 'Inputs', v: '1 prompt + 2 refs' },
              { k: 'Model', v: 'Nano Banana Pro' },
              { k: 'Output', v: '1024 × 1024' },
            ].map((row, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: TEXT.muted, fontSize: 11 }}>{row.k}</span>
                <span style={{ color: TEXT.primary, fontSize: 11, fontWeight: 500, opacity: 0.8 }}>{row.v}</span>
              </div>
            ))}
            {/* Progress bar */}
            <div style={{ height: 3, borderRadius: 2, background: 'rgba(0,0,0,0.04)', marginTop: 4, marginBottom: 14, overflow: 'hidden' }}>
              <div className="hero-progress" style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, #3e4557, #7ea8c9)', opacity: 0.5 }} />
            </div>
            {/* CTA button */}
            <button style={{
              width: '100%', padding: '11px 0', borderRadius: 10, border: 'none', cursor: 'default',
              background: '#3e4557', color: '#fff', fontSize: 12.5, fontWeight: 700,
              fontFamily: 'Inter,system-ui,sans-serif', letterSpacing: '-0.01em',
              boxShadow: '0 4px 14px rgba(62,69,87,0.25)',
            }}>Generate Image</button>
          </div>
        </div>
      </div>

      {/* ── OUTPUT NODE 1 ─────────────────────────────── */}
      <div className={nodeClass('out1')} onPointerDown={e => onPointerDown('out1', e)} style={{ position: 'absolute', left: `${(nodes.out1.x / CANVAS_W) * 100}%`, top: `${(nodes.out1.y / CANVAS_H) * 100}%`, width: `${(nodes.out1.w / CANVAS_W) * 100}%`, zIndex: nodes.out1.z, cursor: 'grab', userSelect: 'none' }}>
        <GlassCard accent={C.output} label="Output" shadow="deep" noPadding>
          <div style={{ aspectRatio: '1/1' }}>
            <img src="/images/hero/output-1.jpeg" alt="Generated output" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        </GlassCard>
      </div>

      {/* ── OUTPUT NODE 2 ─────────────────────────────── */}
      <div className={nodeClass('out2')} onPointerDown={e => onPointerDown('out2', e)} style={{ position: 'absolute', left: `${(nodes.out2.x / CANVAS_W) * 100}%`, top: `${(nodes.out2.y / CANVAS_H) * 100}%`, width: `${(nodes.out2.w / CANVAS_W) * 100}%`, zIndex: nodes.out2.z, cursor: 'grab', userSelect: 'none' }}>
        <GlassCard accent={C.output} label="Variation" shadow="deep" noPadding>
          <div style={{ aspectRatio: '1/1' }}>
            <img src="/images/hero/output-2.jpeg" alt="Generated variation" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        </GlassCard>
      </div>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────────────────
   GlassCard — reusable real-glassmorphism card with backdrop-blur
   ────────────────────────────────────────────────────────────────────────── */

function GlassCard({ accent, label, shadow = 'soft', noPadding = false, children }: {
  accent: typeof C.prompt;
  label: string;
  shadow?: 'soft' | 'deep';
  noPadding?: boolean;
  children: React.ReactNode;
}) {
  const shadowStyle = shadow === 'deep'
    ? '0 12px 48px rgba(140,165,195,0.16), 0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)'
    : '0 6px 28px rgba(140,165,195,0.12), 0 1px 4px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.65)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 14,
      border: '1px solid rgba(255,255,255,0.75)',
      boxShadow: shadowStyle,
      overflow: 'hidden', width: '100%',
    }}>
      <div style={{
        padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0,0,0,0.04)',
        background: 'rgba(255,255,255,0.4)',
      }}>
        <span style={{ color: accent.accent, fontSize: 11, fontWeight: 600, fontFamily: 'Inter,system-ui,sans-serif' }}>{label}</span>
      </div>
      <div style={{ padding: noPadding ? 0 : '10px 12px' }}>{children}</div>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────────────────
   Mobile Hero — vertical stacked real-glass nodes
   ────────────────────────────────────────────────────────────────────────── */

function HeroCanvasMobile() {
  const MobileGlass = ({
    label, accent, children, align = 'left', size = 'normal',
  }: {
    label: string; accent: typeof C.prompt;
    children: React.ReactNode; align?: 'left' | 'right' | 'center'; size?: 'normal' | 'large';
  }) => {
    const w = size === 'large' ? '88%' : '78%';
    const maxW = size === 'large' ? 310 : 270;
    return (
      <div className="hero-glass-node" style={{
        background: 'rgba(255,255,255,0.65)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        border: size === 'large' ? `1.5px solid rgba(255,255,255,0.8)` : '1px solid rgba(255,255,255,0.75)',
        borderRadius: size === 'large' ? 16 : 14,
        width: w, maxWidth: maxW,
        marginLeft: align === 'right' ? 'auto' : align === 'center' ? 'auto' : 0,
        marginRight: align === 'left' ? 'auto' : align === 'center' ? 'auto' : 0,
        overflow: 'hidden',
        boxShadow: size === 'large'
          ? '0 10px 40px rgba(62,69,87,0.12), 0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)'
          : '0 6px 28px rgba(140,165,195,0.12), 0 1px 4px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)',
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.45)', padding: '8px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0,0,0,0.04)',
        }}>
          <span style={{ color: accent.accent, fontSize: size === 'large' ? 13 : 11.5, fontWeight: size === 'large' ? 700 : 600, fontFamily: 'Inter,system-ui,sans-serif' }}>{label}</span>
        </div>
        <div style={{ padding: size === 'large' ? '12px 14px' : '10px 14px' }}>{children}</div>
      </div>
    );
  };

  const MobileEdge = ({ color }: { color: string }) => (
    <div style={{ display: 'flex', justifyContent: 'center', height: 36, margin: '-2px 0', overflow: 'hidden' }}>
      <svg viewBox="0 0 20 36" fill="none" style={{ width: 20, height: 36 }}>
        {/* Glow behind line */}
        <path d="M10,0 L10,36" stroke={color} strokeWidth="8" opacity="0.15" filter="url(#mobileEdgeGlow)" />
        <defs><filter id="mobileEdgeGlow"><feGaussianBlur stdDeviation="3" /></filter></defs>
        <path d="M10,0 L10,36" stroke={color} strokeWidth="2" opacity="0.35" strokeDasharray="4 3">
          <animate attributeName="stroke-dashoffset" from="0" to="-14" dur="2s" repeatCount="indefinite" />
        </path>
        {/* Traveling dot — brighter */}
        <circle r="3.5" fill={color} opacity="0.75">
          <animateMotion dur="3s" repeatCount="indefinite" path="M10,0 L10,36" />
        </circle>
      </svg>
    </div>
  );

  return (
    <div style={{ padding: '0 16px', maxWidth: 360, margin: '0 auto' }}>
      {/* Prompt */}
      <MobileGlass label="Prompt" accent={C.prompt} align="left">
        <p style={{ color: TEXT.primary, fontSize: 12, lineHeight: 1.55, margin: 0, opacity: 0.85 }}>
          Urban fashion shoot in neon-lit city streets, cyberpunk aesthetic
        </p>
        <div style={{ marginTop: 8 }}>
          <span style={{ background: C.prompt.bg, color: C.prompt.accent, fontSize: 9, fontWeight: 600, padding: '3px 10px', borderRadius: 4 }}>Enhance</span>
        </div>
      </MobileGlass>

      <MobileEdge color={C.prompt.accent} />

      {/* Upload */}
      <MobileGlass label="Upload" accent={C.upload} align="right">
        <div style={{ display: 'flex', gap: 5 }}>
          <div style={{ flex: 1, aspectRatio: '1', borderRadius: 7, overflow: 'hidden', background: '#f0f0f0' }}>
            <img src="/images/hero/subject.png" alt="Model reference" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
          <div style={{ flex: 1, aspectRatio: '1', borderRadius: 7, overflow: 'hidden', background: '#f0f0f0' }}>
            <img src="/images/hero/product.jpeg" alt="Product reference" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        </div>
        <p style={{ color: TEXT.muted, fontSize: 10, marginTop: 6, marginBottom: 0 }}>2 references</p>
      </MobileGlass>

      <MobileEdge color={C.upload.accent} />

      {/* Generate — larger, hero of the mobile flow */}
      <MobileGlass label="Generate" accent={C.generate} align="center" size="large">
        {[
          { k: 'Inputs', v: '1 prompt + 2 refs' },
          { k: 'Model', v: 'Nano Banana Pro' },
        ].map((row, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ color: TEXT.muted, fontSize: 11 }}>{row.k}</span>
            <span style={{ color: TEXT.primary, fontSize: 11, fontWeight: 500, opacity: 0.8 }}>{row.v}</span>
          </div>
        ))}
        <div style={{ height: 3, borderRadius: 2, background: 'rgba(0,0,0,0.04)', marginTop: 2, marginBottom: 12, overflow: 'hidden' }}>
          <div className="hero-progress" style={{ height: '100%', borderRadius: 2, background: `linear-gradient(90deg, #3e4557, #7ea8c9)`, opacity: 0.5 }} />
        </div>
        <button style={{
          width: '100%', padding: '11px 0', borderRadius: 10, border: 'none',
          background: '#3e4557', color: '#fff', fontSize: 12.5, fontWeight: 700,
          fontFamily: 'Inter,system-ui,sans-serif',
          boxShadow: '0 4px 14px rgba(62,69,87,0.25)',
        }}>Generate Image</button>
      </MobileGlass>

      <MobileEdge color={C.generate.accent} />

      {/* Output */}
      <MobileGlass label="Output" accent={C.output} align="right">
        <div style={{ borderRadius: 8, overflow: 'hidden', aspectRatio: '1/1' }}>
          <img src="/images/hero/output-1.jpeg" alt="Generated output" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      </MobileGlass>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────────────────
   Landing Page
   ────────────────────────────────────────────────────────────────────────── */

export function Landing() {
  const { user } = useAuthStore();

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader overlay />

      <main>
        {/* ── HERO ──────────────────────────────────────────── */}
        <section className="relative overflow-x-clip overflow-y-visible" style={{ background: '#fafbfd' }}>

          {/* ── Light blue tinted background behind dot grid ── */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
            background: 'linear-gradient(180deg, rgba(218,236,246,0.18) 0%, rgba(230,240,250,0.12) 40%, rgba(238,227,243,0.08) 70%, transparent 100%)',
          }} />

          {/* ── Dot grid pattern — stronger, elevates glass nodes ── */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
            backgroundImage: 'radial-gradient(circle, rgba(62,69,87,0.13) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }} />

          {/* ── Soft color washes — blue + pink/lavender ── */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            {/* Blue wash — left side */}
            <div className="hero-caustic" style={{
              position: 'absolute', top: '10%', left: '0%', width: '55%', height: '70%',
              background: 'radial-gradient(ellipse at 40% 50%, rgba(218,236,246,0.28), rgba(190,218,238,0.14) 50%, transparent 80%)',
              filter: 'blur(70px)',
            }} />
            {/* Pink/lavender wash — right side */}
            <div style={{
              position: 'absolute', top: '15%', right: '0%', width: '50%', height: '65%',
              background: 'radial-gradient(ellipse at 60% 40%, rgba(238,227,243,0.30), rgba(191,173,211,0.12) 50%, transparent 80%)',
              filter: 'blur(70px)',
            }} />
            {/* Secondary — center blend */}
            <div style={{
              position: 'absolute', top: '40%', left: '25%', width: '50%', height: '35%',
              background: 'radial-gradient(circle, rgba(218,236,246,0.10), rgba(238,227,243,0.08) 50%, transparent 80%)',
              filter: 'blur(50px)',
            }} />
          </div>

          {/* Noise grain — subtle texture */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.015]" aria-hidden="true" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'256\' height=\'256\' filter=\'url(%23n)\' opacity=\'1\'/%3E%3C/svg%3E")', backgroundRepeat: 'repeat' }} />

          {/* ── Unified hero zone: text overlays canvas ── */}
          <div className="relative z-10">
            {/* Text content — centered, higher z-index */}
            <div className="relative z-20 pt-24 md:pt-[7.5rem] pointer-events-none">
              {/* Badge */}
              <div className="text-center mb-3 pointer-events-auto">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gray-200/50 bg-white/80 text-[13px] backdrop-blur-md shadow-sm" style={{ color: TEXT.secondary }}>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#b5a0cc' }} />
                  60+ AI models for image, video & text
                </span>
              </div>

              {/* Headline + subtitle */}
              <div className="text-center max-w-3xl mx-auto px-6 mb-3">
                <h1 className="text-[2.25rem] sm:text-[3rem] md:text-[3.75rem] lg:text-[4.25rem] leading-[1.05] mb-2" style={{ color: TEXT.primary, letterSpacing: '-0.025em', fontFamily: "'Playfair Display',Georgia,serif", fontWeight: 600 }}>
                  Every AI model.{' '}
                  <br />
                  <em style={{ color: '#6b529e', fontStyle: 'italic', fontWeight: 400 }}>
                    One visual canvas.
                  </em>
                </h1>
                <p className="text-base sm:text-lg max-w-lg mx-auto leading-relaxed" style={{ color: TEXT.secondary, fontFamily: 'Inter,system-ui,sans-serif' }}>
                  Connect prompts, references, and presets on a node-based canvas. Generate with Nano Banana, FLUX, Sora, Kling, and 60+ more.
                </p>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center px-6 pointer-events-auto">
                <Link to={user ? '/canvas' : '/auth?tab=register'}>
                  <button className="px-7 py-3.5 rounded-full text-white font-semibold text-[15px] transition-all hover:scale-[1.03] active:scale-[0.98]" style={{
                    background: '#1d2939',
                    boxShadow: '0 2px 12px rgba(29,41,57,0.25), 0 1px 3px rgba(0,0,0,0.1)',
                    fontFamily: 'Inter,system-ui,sans-serif', letterSpacing: '-0.01em',
                  }}>
                    {user ? 'Open Canvas' : IS_ALPHA ? 'Start Creating' : 'Start Creating'}
                    <ArrowRight className="w-4 h-4 ml-2 inline" />
                  </button>
                </Link>
                <a href="#how-it-works" className="px-7 py-3.5 rounded-full font-medium text-[15px] border border-gray-200/80 hover:border-gray-300 hover:bg-white/80 transition-all backdrop-blur-sm" style={{ color: TEXT.secondary, fontFamily: 'Inter,system-ui,sans-serif' }}>
                  See How It Works
                </a>
              </div>
              <p className="text-center mt-3 text-[13px] pointer-events-auto" style={{ color: '#6b7a8a' }}>
                {IS_ALPHA
                  ? <>7-day free trial &middot; All models included</>
                  : <>60+ AI models &middot; One visual canvas</>}
              </p>
            </div>

            {/* Canvas layer — behind text, overlapping the full hero */}
            <div className="hidden lg:block" style={{ marginTop: -145, overflow: 'visible' }}>
              <div className="px-4 md:px-8 lg:px-12" style={{ overflow: 'visible' }}>
                <HeroCanvasDesktop />
              </div>
            </div>
            <div className="block lg:hidden mt-4">
              <HeroCanvasMobile />
            </div>
          </div>

          {/* Bottom fade */}
          <div className="h-12 md:h-16" style={{ background: 'linear-gradient(180deg, transparent 0%, #ffffff 100%)' }} />
        </section>

        <LandingProblem />
        <LandingSteps />
        <LandingModels />
        <LandingDifferentiators />
        <LandingCollab />
        <LandingPricing />
        <LandingFaq />
        <LandingCta />

      </main>


      <PublicFooter />

      <style>{`
        /* ── Float animation — gentle, organic ─── */
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes heroFloatAlt {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }

        /* ── Progress bar sweep ─── */
        @keyframes heroProgress {
          0% { width: 0; opacity: 0.3; }
          45% { width: 100%; opacity: 0.6; }
          55% { width: 100%; opacity: 0.6; }
          100% { width: 0; opacity: 0.3; }
        }

        /* ── Status dot pulse ─── */
        @keyframes heroStatusPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }

        /* ── Cursor blink ─── */
        @keyframes heroCursorBlink {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0; }
        }

        /* ── Caustic slow drift ─── */
        @keyframes heroCausticDrift {
          0%, 100% { transform: translateX(0) translateY(0) scale(1); }
          33% { transform: translateX(15px) translateY(-8px) scale(1.02); }
          66% { transform: translateX(-10px) translateY(5px) scale(0.98); }
        }

        /* ── Prism halo breathe ─── */
        @keyframes heroPrismBreathe {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.04); }
        }

        .hero-glass-node {
          animation: heroFloat 7s ease-in-out infinite;
          transition: box-shadow 0.2s ease;
        }
        .hero-glass-node:nth-child(even) {
          animation-name: heroFloatAlt;
          animation-duration: 9s;
        }

        .hero-glass-node:hover {
          cursor: grab;
        }

        .hero-node-dragging {
          animation: none !important;
          cursor: grabbing !important;
          z-index: 50 !important;
          filter: drop-shadow(0 12px 32px rgba(0,0,0,0.12));
          transition: filter 0.15s ease;
        }

        .hero-generate {
          animation-duration: 8s !important;
        }

        .hero-progress {
          animation: heroProgress 6s ease-in-out infinite;
        }

        .hero-status-dot {
          animation: heroStatusPulse 2.5s ease-in-out infinite;
        }

        .hero-cursor {
          animation: heroCursorBlink 1.3s ease-in-out infinite;
        }

        .hero-caustic {
          animation: heroCausticDrift 20s ease-in-out infinite;
        }

        .hero-prism-halo {
          animation: heroPrismBreathe 5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}